import type { DataGridColumn } from "@heroui-pro/react";
import type { SortDescriptor } from "react-aria-components";
import type { ReactNode } from "react";

import { ListBox, Pagination, Select, Skeleton } from "@heroui/react";
import { DataGrid } from "@heroui-pro/react";
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

export type { DataGridColumn, SortDescriptor };

/** Plain header: labels and a hairline, no grey band and no column separators; the header sticks while the body scrolls. */
const PLAIN_HEADER =
  "min-h-0 flex-1 [&_thead]:sticky [&_thead]:top-0 [&_thead]:z-10 [&_thead]:border-b [&_thead]:border-separator [&_thead]:bg-surface [&_th]:bg-transparent [&_th]:after:hidden";

export const PAGE_SIZES = [10, 12, 25, 50];

export type TablePagination = {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  /** When set the range reads "Showing 1 - 10 of 12 carts" instead of "1–10 of 12". */
  itemLabel?: string;
  /** Options of the Rows select (default `PAGE_SIZES`). */
  pageSizes?: number[];
};

/** Client-side sorting and paging for a list that is fetched whole. Pages with server paging pass their own state to `DataTable`. */
export function useClientTable<T>(
  rows: T[],
  {
    columns,
    initialSort,
    pageSize: initialPageSize = 12,
  }: {
    columns: DataGridColumn<T>[];
    initialSort?: SortDescriptor;
    pageSize?: number;
  },
) {
  const [sort, setSort] = useState<SortDescriptor | undefined>(initialSort);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const sorted = useMemo(() => {
    const column = columns.find((c) => c.id === sort?.column);

    if (!sort || !column) return rows;
    const compare =
      column.sortFn ??
      ((a: T, b: T) => {
        const key = column.accessorKey as keyof T;

        return String(a[key] ?? "").localeCompare(
          String(b[key] ?? ""),
          undefined,
          { numeric: true },
        );
      });
    const direction = sort.direction === "descending" ? -1 : 1;

    return [...rows].sort((a, b) => compare(a, b) * direction);
  }, [rows, columns, sort]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  return {
    rows: sorted.slice((page - 1) * pageSize, page * pageSize),
    sort,
    onSortChange: setSort,
    pagination: {
      page,
      pageSize,
      total: sorted.length,
      onPageChange: setPage,
      onPageSizeChange: (size: number) => {
        setPageSize(size);
        setPage(1);
      },
    } satisfies TablePagination,
  };
}

function pageList(
  page: number,
  count: number,
): Array<number | "gap-start" | "gap-end"> {
  if (count <= 7) return Array.from({ length: count }, (_, i) => i + 1);
  if (page <= 4) return [1, 2, 3, 4, 5, "gap-end", count];
  if (page >= count - 3)
    return [1, "gap-start", count - 4, count - 3, count - 2, count - 1, count];

  return [1, "gap-start", page - 1, page, page + 1, "gap-end", count];
}

export function TablePager({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  itemLabel,
  pageSizes = PAGE_SIZES,
}: TablePagination) {
  const count = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-1 pt-3">
      <div className="text-muted flex items-center gap-3 text-sm tabular-nums">
        <span>
          {itemLabel
            ? `Showing ${from} - ${to} of ${total} ${itemLabel}`
            : `${from}–${to} of ${total}`}
        </span>
        {onPageSizeChange ? (
          <span aria-hidden="true">Rows</span>
        ) : null}
        {onPageSizeChange ? (
          <Select
            aria-label="Rows per page"
            className="w-20"
            selectedKey={String(pageSize)}
            variant="primary"
            onSelectionChange={(key) => onPageSizeChange(Number(key))}
          >
            <Select.Trigger>
              <Select.Value />
              <Select.Indicator />
            </Select.Trigger>
            <Select.Popover>
              <ListBox>
                {pageSizes.map((size) => (
                  <ListBox.Item
                    key={size}
                    id={String(size)}
                    textValue={String(size)}
                  >
                    {size}
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                ))}
              </ListBox>
            </Select.Popover>
          </Select>
        ) : null}
      </div>
      {count > 1 ? (
        <Pagination className="w-auto" size="sm">
          <Pagination.Content>
            <Pagination.Item>
              <Pagination.Previous
                isDisabled={page === 1}
                onPress={() => onPageChange(page - 1)}
              >
                <ChevronLeft className="size-4" />
                <span>Previous</span>
              </Pagination.Previous>
            </Pagination.Item>
            {pageList(page, count).map((entry) =>
              typeof entry === "number" ? (
                <Pagination.Item key={entry}>
                  <Pagination.Link
                    aria-label={`pagination item ${entry}`}
                    isActive={entry === page}
                    onPress={() => onPageChange(entry)}
                  >
                    {entry}
                  </Pagination.Link>
                </Pagination.Item>
              ) : (
                <Pagination.Item key={entry}>
                  <Pagination.Ellipsis />
                </Pagination.Item>
              ),
            )}
            <Pagination.Item>
              <Pagination.Next
                isDisabled={page === count}
                onPress={() => onPageChange(page + 1)}
              >
                <span>Next</span>
                <ChevronRight className="size-4" />
              </Pagination.Next>
            </Pagination.Item>
          </Pagination.Content>
        </Pagination>
      ) : null}
    </div>
  );
}

function GridSkeleton({ columns }: { columns: number }) {
  return (
    <div
      aria-busy="true"
      aria-label="Loading"
      className="flex flex-1 flex-col gap-1 overflow-hidden"
      role="status"
    >
      <div className="border-separator h-10 border-b" />
      {Array.from({ length: 8 }, (_, row) => (
        <div
          key={row}
          className="border-separator flex h-[52px] items-center gap-6 border-b px-4"
        >
          {Array.from({ length: columns }, (_, col) => (
            <Skeleton key={col} className="h-4 flex-1 rounded-md" />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * The page table: a plain header with sorting in the column headers, a body that scrolls under a sticky header,
 * and the pager pinned below. `rows` is the current page; sorting and paging state live with the caller
 * (`useClientTable` does it for a list fetched whole).
 */
export function DataTable<T extends object>({
  label,
  rows,
  columns,
  getRowId,
  sort,
  onSortChange,
  pagination,
  isLoading,
  empty,
  onRowAction,
  variant = "secondary",
  compact,
  selection,
  minTableWidth = columns.length * 100,
}: {
  label: string;
  rows: T[];
  columns: DataGridColumn<T>[];
  getRowId: (row: T) => string | number;
  sort?: SortDescriptor;
  onSortChange?: (sort: SortDescriptor) => void;
  pagination?: TablePagination;
  isLoading?: boolean;
  empty?: { title: string; description?: string; action?: ReactNode };
  onRowAction?: (key: string | number) => void;
  variant?: "primary" | "secondary";
  /** Tighter cell padding, for rows that hold an avatar and two lines (about 52px tall). */
  compact?: boolean;
  /** Smallest width of the table in px; narrower screens scroll sideways instead of squeezing the columns. Defaults to 100px a column. */
  minTableWidth?: number;
  /** Row checkboxes with a select-all in the header; the caller owns the set of selected row ids. */
  selection?: {
    keys: Set<string | number>;
    onChange: (keys: Set<string | number>) => void;
    /** Ids of every row on this page, for select-all. */
    allKeys: Array<string | number>;
  };
}) {
  // A sortable column shows a quiet up/down icon until it is sorted, then the direction.
  const shownColumns = columns.map((column) => {
    const label = column.header;

    return column.allowsSorting && typeof label !== "function"
      ? {
          ...column,
          header: ({ sortDirection }: { sortDirection?: string }) => (
            <span className="inline-flex items-center gap-1">
              {label}
              {sortDirection === "ascending" ? (
                <ArrowUp aria-hidden="true" className="size-3" />
              ) : sortDirection === "descending" ? (
                <ArrowDown aria-hidden="true" className="size-3" />
              ) : (
                <ChevronsUpDown
                  aria-hidden="true"
                  className="text-muted size-3"
                />
              )}
            </span>
          ),
        }
      : column;
  });

  return (
    // `isolate` keeps the sticky header's z-index inside the table, so dialogs and sheets always draw over it.
    <div
      className="@container isolate flex min-h-0 flex-1 flex-col"
      style={{ "--table-min": `${minTableWidth}px` } as React.CSSProperties}
    >
      {isLoading ? (
        <GridSkeleton columns={columns.length} />
      ) : (
        <DataGrid
          aria-label={label}
          className={compact ? `${PLAIN_HEADER} [&_td]:px-3 [&_td]:py-2 [&_th]:px-3` : PLAIN_HEADER}
          columns={shownColumns}
          contentClassName="min-w-(--table-min)"
          data={rows}
          getRowId={getRowId}
          renderEmptyState={() => (
            // Pinned to the visible part of a table that scrolls sideways, so a phone still sees the message.
            <div className="sticky left-0 me-auto flex w-full max-w-[100cqw] flex-col items-center gap-1 self-start py-16 text-center">
              <p className="font-medium">
                {empty?.title ?? "Nothing here yet"}
              </p>
              {empty?.description ? (
                <p className="text-muted text-sm">{empty.description}</p>
              ) : null}
              {empty?.action ? (
                <div className="mt-3">{empty.action}</div>
              ) : null}
            </div>
          )}
          scrollContainerClassName="h-full overflow-auto"
          selectedKeys={selection?.keys}
          selectionMode={selection ? "multiple" : undefined}
          showSelectionCheckboxes={Boolean(selection)}
          sortDescriptor={sort}
          variant={variant}
          onRowAction={onRowAction}
          onSelectionChange={
            selection
              ? (next) =>
                  selection.onChange(
                    next === "all"
                      ? new Set(selection.allKeys)
                      : new Set(next as Set<string | number>),
                  )
              : undefined
          }
          onSortChange={onSortChange}
        />
      )}
      {pagination && !isLoading ? <TablePager {...pagination} /> : null}
    </div>
  );
}
